// Đo tại chỗ (TECH_BASELINE §9.4): khung hình từ ticker; thời gian JS update mỗi khung (mọi listener
// trước lệnh render: sim, scene, hạt, spine); thời gian lệnh render (dựng + nộp lệnh GL, gồm cả lúc
// chờ GPU khi hàng lệnh đầy, nên trên GPU phần mềm nó phình theo tốc độ tô); số draw call.
// `window.__ht.perf.snapshot()` trả JSON cho tools/perf.
import { UPDATE_PRIORITY, type Application } from 'pixi.js';

const WINDOW = 600;

export interface PerfSnapshot {
  frames: number;
  fps: number;
  p95Ms: number;
  /** JS update mỗi khung (ms, không gồm lệnh render): trung bình và p95 trên cửa sổ trượt. */
  jsAvgMs: number;
  jsP95Ms: number;
  /** Lệnh render mỗi khung (ms), trung bình. */
  renderAvgMs: number;
  drawCallsAvg: number;
  particles: number;
  quality: number;
}

export function attachPerfProbe(app: Application, particles: () => number): { snapshot(): PerfSnapshot } {
  const dts = new Float64Array(WINDOW);
  const js = new Float64Array(WINDOW);
  const rd = new Float64Array(WINDOW);
  let updateEnd = 0;
  let n = 0;
  let frameStart = 0;
  let draws = 0;
  let drawFrames = 0;
  let drawTotal = 0;

  const gl = (app.renderer as unknown as { gl?: WebGLRenderingContext }).gl;
  if (gl) {
    const de = gl.drawElements.bind(gl);
    const da = gl.drawArrays.bind(gl);
    gl.drawElements = (...a: Parameters<WebGLRenderingContext['drawElements']>) => (draws++, de(...a));
    gl.drawArrays = (...a: Parameters<WebGLRenderingContext['drawArrays']>) => (draws++, da(...a));
  }
  // Mốc: trước mọi listener (ưu tiên cao nhất) · ngay trước lệnh render của Application (render chạy
  // ở LOW) · sau lệnh render (UTILITY).
  app.ticker.add(() => (frameStart = performance.now()), undefined, UPDATE_PRIORITY.INTERACTION + 1);
  app.ticker.add(() => (updateEnd = performance.now()), undefined, UPDATE_PRIORITY.LOW + 1);
  app.ticker.add(
    () => {
      const i = (n - 1 + WINDOW) % WINDOW;
      js[i] = updateEnd - frameStart;
      rd[i] = performance.now() - updateEnd;
    },
    undefined,
    UPDATE_PRIORITY.UTILITY,
  );
  app.ticker.add(() => {
    dts[n % WINDOW] = app.ticker.deltaMS;
    n++;
    drawTotal += draws;
    drawFrames++;
    draws = 0;
  });

  return {
    snapshot() {
      const len = Math.min(n, WINDOW);
      const arr = Array.from(dts.subarray(0, len)).sort((a, b) => a - b);
      const sum = arr.reduce((a, b) => a + b, 0);
      const jarr = Array.from(js.subarray(0, len)).sort((a, b) => a - b);
      return {
        jsAvgMs: len ? jarr.reduce((a, b) => a + b, 0) / len : 0,
        jsP95Ms: jarr[Math.floor(len * 0.95)] ?? 0,
        renderAvgMs: len ? Array.from(rd.subarray(0, len)).reduce((a, b) => a + b, 0) / len : 0,
        frames: n,
        fps: sum > 0 ? (len * 1000) / sum : 0,
        p95Ms: arr[Math.floor(len * 0.95)] ?? 0,
        drawCallsAvg: drawFrames ? drawTotal / drawFrames : 0,
        particles: particles(),
        quality: 0,
      };
    },
  };
}
